import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, FileSpreadsheet, FileDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";
import { toast } from "sonner";

export function UnresolvedTdsVendors({ filters }) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(filters || {});
  useEffect(() => {
    axios.get(`${API}/reports/tds-unresolved-vendors`, { params: filters }).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: { vendors: 0, deductions: 0, tds: 0 } }));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/reports/tds-unresolved-vendors/export`, { params: { format, ...filters }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `tds-unresolved-vendors.${format}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (!data || data.rows.length === 0) return null;
  const t = data.totals;
  return (
    <Card className="border-amber-200 shadow-sm" data-testid="tds-unresolved-card">
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0 gap-4">
        <div>
          <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Vendors whose TDS code SAP cannot provide</CardTitle>
          <p className="text-xs text-slate-500 mt-1" data-testid="tds-unresolved-summary">
            <b>{t.vendors}</b> vendors · {t.deductions} deductions · TDS <b>{inrShort(t.tds)}</b> shown under "XX". SAP's journal posts the placeholder code <code>XX</code>; the real code comes from the supplier master — fix it there (Business Partner → Company Code → Withholding Tax) and the next sync will classify these.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="tds-unresolved-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="tds-unresolved-export-csv"><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table data-testid="tds-unresolved-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-[11px] uppercase tracking-wider">Vendor</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-right">Deductions</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-right">TDS deducted</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-right">Observed rate</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider">Last deduction</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider">What to fix in SAP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((v) => (
              <TableRow key={v.party_code} data-testid={`tds-unresolved-row-${v.party_code}`}>
                <TableCell className="text-sm"><span className="font-semibold text-slate-800">{v.party_name}</span><span className="block text-[11px] text-slate-400 font-mono">{v.party_code}{v.pan ? ` · PAN ${v.pan}` : ""}</span></TableCell>
                <TableCell className="text-right text-sm tabular-nums">{v.deductions}</TableCell>
                <TableCell className="text-right text-sm font-semibold text-rose-700 tabular-nums">{inr(v.tds)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{v.rate_pct != null ? `${v.rate_pct}%` : "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{v.last_date || "—"}</TableCell>
                <TableCell className="text-xs text-slate-600">{v.reason_text}{v.master_codes?.length ? <span className="block text-[11px] text-slate-400">Master codes: {v.master_codes.join(", ")}</span> : null}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
